/** @typedef {'top' | 'bottom' | 'left' | 'right'} OverlayPlacement */

/**
 * @typedef {{ x: number; y: number; width: number; height: number }} RectBounds
 */

/**
 * @param {RectBounds} a
 * @param {RectBounds} b
 * @returns {boolean}
 */
export function boundsOverlap(a, b) {
  return a.x < b.x + b.width && a.x + a.width > b.x && a.y < b.y + b.height && a.y + a.height > b.y;
}

/**
 * @param {{ x: number; y: number }} anchor
 * @param {number} [size]
 * @returns {RectBounds}
 */
export function anchorPointToBounds(anchor, size = 8) {
  const half = size / 2;
  return {
    x: anchor.x - half,
    y: anchor.y - half,
    width: size,
    height: size,
  };
}

/**
 * @param {OverlayPlacement} placement
 * @param {RectBounds} anchorBounds
 * @param {{ width: number; height: number }} overlaySize
 * @param {number} gap
 * @returns {{ x: number; y: number }}
 */
function computePlacementPosition(placement, anchorBounds, overlaySize, gap) {
  const anchorCenterX = anchorBounds.x + anchorBounds.width / 2;
  const anchorCenterY = anchorBounds.y + anchorBounds.height / 2;

  switch (placement) {
    case 'top':
      return {
        x: anchorCenterX - overlaySize.width / 2,
        y: anchorBounds.y - overlaySize.height - gap,
      };
    case 'bottom':
      return {
        x: anchorCenterX - overlaySize.width / 2,
        y: anchorBounds.y + anchorBounds.height + gap,
      };
    case 'left':
      return {
        x: anchorBounds.x - overlaySize.width - gap,
        y: anchorCenterY - overlaySize.height / 2,
      };
    case 'right':
      return {
        x: anchorBounds.x + anchorBounds.width + gap,
        y: anchorCenterY - overlaySize.height / 2,
      };
    default:
      return { x: anchorCenterX - overlaySize.width / 2, y: anchorBounds.y - overlaySize.height - gap };
  }
}

/**
 * @param {{ x: number; y: number }} position
 * @param {{ width: number; height: number }} overlaySize
 * @param {RectBounds} canvasBounds
 * @param {RectBounds[]} blockedBounds
 * @param {OverlayPlacement} placement
 * @returns {number}
 */
function scorePlacement(position, overlaySize, canvasBounds, blockedBounds, placement) {
  const overlayRect = {
    x: position.x,
    y: position.y,
    width: overlaySize.width,
    height: overlaySize.height,
  };

  let score = 100;

  if (
    overlayRect.x < canvasBounds.x ||
    overlayRect.y < canvasBounds.y ||
    overlayRect.x + overlayRect.width > canvasBounds.x + canvasBounds.width ||
    overlayRect.y + overlayRect.height > canvasBounds.y + canvasBounds.height
  ) {
    score -= 60;
  }

  for (const blocked of blockedBounds) {
    if (boundsOverlap(overlayRect, blocked)) {
      score -= 40;
    }
  }

  const preferenceBoost = { top: 4, bottom: 3, right: 2, left: 1 };
  score += preferenceBoost[placement] ?? 0;

  return score;
}

/**
 * @param {{
 *   anchorBounds: RectBounds;
 *   overlaySize: { width: number; height: number };
 *   canvasBounds: RectBounds;
 *   blockedBounds?: RectBounds[];
 *   preferredPlacements?: OverlayPlacement[];
 *   gap?: number;
 * }} params
 * @returns {{ placement: OverlayPlacement; x: number; y: number; score: number }}
 */
export function getBestOverlayPlacement({
  anchorBounds,
  overlaySize,
  canvasBounds,
  blockedBounds = [],
  preferredPlacements = ['top', 'bottom', 'right', 'left'],
  gap = 14,
}) {
  const candidates = preferredPlacements.map((placement) => {
    const position = computePlacementPosition(placement, anchorBounds, overlaySize, gap);
    let { x, y } = position;

    const overlayRect = { x, y, width: overlaySize.width, height: overlaySize.height };
    if (overlayRect.x < canvasBounds.x) x = canvasBounds.x + 4;
    if (overlayRect.y < canvasBounds.y) y = canvasBounds.y + 4;
    if (overlayRect.x + overlaySize.width > canvasBounds.x + canvasBounds.width) {
      x = canvasBounds.x + canvasBounds.width - overlaySize.width - 4;
    }
    if (overlayRect.y + overlaySize.height > canvasBounds.y + canvasBounds.height) {
      y = canvasBounds.y + canvasBounds.height - overlaySize.height - 4;
    }

    const score = scorePlacement({ x, y }, overlaySize, canvasBounds, blockedBounds, placement);
    return { placement, x, y, score };
  });

  candidates.sort((a, b) => b.score - a.score);
  return candidates[0] ?? { placement: 'top', x: 0, y: 0, score: 0 };
}

/**
 * @param {OverlayPlacement} placement
 * @returns {OverlayPlacement}
 */
export function oppositePlacement(placement) {
  switch (placement) {
    case 'top':
      return 'bottom';
    case 'bottom':
      return 'top';
    case 'left':
      return 'right';
    case 'right':
      return 'left';
    default:
      return 'bottom';
  }
}

/**
 * Default chrome regions that overlay the canvas (nav, panel, toolbar, view switcher).
 * @param {number} stageWidth
 * @param {number} stageHeight
 * @param {{ subcategoryPanelOpen?: boolean; panelWidth?: number }} [options]
 * @returns {RectBounds[]}
 */
export function getDefaultFloorPlanBlockedBounds(stageWidth, stageHeight, options = {}) {
  const inset = 12;
  const navWidth = 64;
  const gap = 12;
  const panelWidth = options.subcategoryPanelOpen === false ? 0 : (options.panelWidth ?? 280);
  const leftChrome = inset + navWidth + gap + panelWidth + gap;
  return [
    { x: 0, y: 0, width: leftChrome, height: stageHeight },
    { x: 0, y: 0, width: stageWidth, height: inset + 52 },
    { x: Math.max(0, stageWidth - 140), y: 0, width: 140, height: inset + 52 },
    { x: Math.max(0, stageWidth - 140), y: Math.max(0, stageHeight - 72), width: 140, height: 72 },
  ];
}
